import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField as MuiTextField,
  InputAdornment,
  Typography,
  useMediaQuery,
  FormControlLabel as MuiFormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useLocation, useParams } from "react-router-dom";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { ArrowForward, Clear } from "@mui/icons-material";
import {
  getTerminalDetails,
  getTerminalDetailsWithActions,
} from "../../../services/services";
import { MPI_TYPE, PAYMENT_HOST_LIST, SUB_MERCHANT_COUNTRY_CODE } from "../../../utils/constants";
import { linkTextStyle, styles } from "../../../utils/styles";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { showFiuuDynamicField, showFiuuFixedFeeDynamicField, showAllinpayDynamicField, showHostFiuuDynamicField, showCybersourceDynamicField, showDashDynamicField, showAliPayDynamicField, showShopeePayMYDynamicField, showWeChatDynamicField } from "../../../utils/terminal_dynamic_util";
import { toNumberInputString } from "../../../utils/appUtils";

const normalizeComparisonValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") {
    return JSON.stringify(
      Object.keys(value)
        .sort()
        .reduce((result, key) => {
          result[key] = normalizeComparisonValue(value[key]);
          return result;
        }, {})
    );
  }
  return String(value);
};

const displayComparisonValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ") || "N/A";
  if (typeof value === "object") {
    return value.name || value.business_name || value.mode || JSON.stringify(value);
  }
  return String(value);
};

const InlineComparison = ({ before, after, forceHighlight = false, disableHighlight = false }) => {
  const changed =
    forceHighlight || normalizeComparisonValue(before) !== normalizeComparisonValue(after);

  if (!changed) return displayComparisonValue(after);

  return (
    <Stack
      component="span"
      direction="row"
      spacing={1.25}
      alignItems="center"
      flexWrap="wrap"
      sx={{ display: "inline-flex", verticalAlign: "middle" }}
    >
      <Box
        component="span"
        sx={{
          color: "text.secondary",
          textDecoration: disableHighlight ? "none" : "line-through",
          overflowWrap: "anywhere",
        }}
      >
        {displayComparisonValue(before)}
      </Box>
      <ArrowForward sx={{ color: "text.secondary", fontSize: 18 }} />
      <Box
        component="span"
        sx={{
          px: disableHighlight ? 0 : 1,
          py: disableHighlight ? 0 : 0.5,
          borderRadius: 1,
          bgcolor: disableHighlight ? "transparent" : "#e8f5e9",
          color: disableHighlight ? "text.primary" : "success.dark",
          fontWeight: disableHighlight ? 400 : 600,
          overflowWrap: "anywhere",
        }}
      >
        {displayComparisonValue(after)}
      </Box>
    </Stack>
  );
};

const routeFieldLabel = (path) => {
  const aliases = {
    "payment_method": "Payment Method",
    "payment_brand": "Payment Brand",
    "payment_host": "Payment Host",
    "currencies": "Currency",
    "default_bin_routing": "Default BIN Routing",
    "mpi_type": "MPI Type",
  };
  if (aliases[path]) return aliases[path];

  return path
    .replace(/^dynamic_info\./, "")
    .replace(/^charges\./, "")
    .replace(/^gw2_proxy_route\./, "GW2 ")
    .replace(/\./g, " ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const flattenRouteValues = (value, prefix = "", result = {}) => {
  if (value === null || value === undefined) {
    if (prefix) result[prefix] = value;
    return result;
  }

  if (Array.isArray(value)) {
    if (prefix === "currencies") {
      result[prefix] = value.map((item) => item.currency).filter(Boolean).join(", ");
      value.forEach((item) => {
        if (item?.currency && item?.min_amount !== undefined) {
          result[`currency ${item.currency} min amount`] = item.min_amount;
        }
      });
    } else if (value.every((item) => item && typeof item === "object")) {
      if (prefix.endsWith("currency")) {
        result[prefix] = value.map((item) => item.currency || item.name).filter(Boolean).join(", ");
      }
      value.forEach((item, index) => {
        flattenRouteValues(item, value.length === 1 ? prefix : `${prefix}.${index}`, result);
      });
    } else if (prefix) {
      result[prefix] = value.map((item) =>
        typeof item === "object" ? JSON.stringify(item) : item
      );
    }
    return result;
  }

  if (typeof value === "object") {
    Object.entries(value).forEach(([key, nestedValue]) => {
      if (
        [
          "id", "original_id", "action", "action_message", "is_active", "is_suspended",
          "created_at", "created_by", "updated_at", "updated_by", "approved_at", "approved_by",
          "reject_message",
        ].includes(key) ||
        key.endsWith("_dynamic_info_id")
      ) {
        return;
      }
      flattenRouteValues(nestedValue, prefix ? `${prefix}.${key}` : key, result);
    });
    return result;
  }

  if (prefix) result[prefix] = value;
  return result;
};

const comparableRouteValues = (route) => {
  if (!route) return {};
  const routeFields = Object.fromEntries(
    Object.entries(route).filter(([key]) => ![
      "payment_charges_pending_payment_mapping",
      "payment_charges_payment_mapping",
      "dynamic_info",
      "original",
    ].includes(key))
  );
  const chargeSource =
    route.payment_charges_pending_payment_mapping ||
    route.payment_charges_payment_mapping ||
    [];
  const charges = Array.isArray(chargeSource) ? (chargeSource[0] || {}) : chargeSource;

  return flattenRouteValues({
    ...routeFields,
    payment_method: route.payment_method?.name,
    payment_brand: route.payment_brand?.name,
    payment_host: route.payment_host?.name,
    currencies: route.currencies || [],
    charges,
    dynamic_info: {
      ...(route.dynamic_info || {}),
      enable_vis: Boolean(route.dynamic_info?.vis_dynamic_info),
    },
    default_bin_routing: route.default_bin_routing,
    mpi_type: route.mpi_type,
    mpi_id: route.mpi_id
      ? `${route.mpi_id.acquirer_bin} - ${route.mpi_id.acquirer_merchant_id}`
      : null,
    gw2_proxy_route: route.gw2_proxy_route || {},
  });
};

const routeComparisonAliases = {
  "charges.base_rate": ["Base Rate", "Base Rate (Wallet)"],
  "charges.markup_rate": ["Markup Rate", "Markup Rate (Wallet)"],
  "charges.base_rate_foreign": ["Base Rate (Foreign Card)"],
  "charges.markup_rate_foreign": ["Markup Rate (Foreign Card)"],
  "charges.base_rate_installment": ["Base Rate (Installment)"],
  "charges.markup_rate_installment": ["Markup Rate (Installment)"],
  "dynamic_info.is_autodebit_enabled": ["AutoDebit Enabled"],
  "dynamic_info.paynow_descriptor_name": ["PayNow Descriptor"],
  "dynamic_info.ref_2": ["PayNow Suffix"],
  "dynamic_info.username": ["Atome Username"],
  "dynamic_info.password": ["Atome Password"],
  "dynamic_info.pf_id": ["Sub Merchant PF ID"],
  "dynamic_info.sub_name": ["Sub Merchant Name"],
  "dynamic_info.sub_id": ["Sub Merchant ID"],
  "dynamic_info.sub_street": ["Sub Merchant Street"],
  "dynamic_info.sub_city": ["Sub Merchant City"],
  "dynamic_info.sub_country_code": ["Sub Merchant Country Code"],
  "dynamic_info.sub_postal_code": ["Sub Merchant Postal Code"],
  "dynamic_info.vis_dynamic_info.partner_merchant_reference_id": ["Partner Merchant Reference ID"],
  "dynamic_info.enabled_3ds": ["Enable 3DS"],
  "dynamic_info.enable_vis": ["Enable VIS"],
  "dynamic_info.grabpay_merchant_id": ["GrabPay Store ID"],
  "dynamic_info.payment_expiry_time": ["Payment Expiry Time (Min)"],
  "dynamic_info.min_amt_postpaid": ["Min Amount (Postpaid)"],
  "dynamic_info.min_amt_installment_4": ["Min Amount (Installment)"],
  "dynamic_info.touchngo_expiry_time": ["Expiry Time (Min)"],
  "dynamic_info.whichever_higher_mdr_cents": ["Minimum MDR"],
  "dynamic_info.mdr_less_than_micro_transaction_amount_cents": ["Micro Transaction MDR"],
  "dynamic_info.cybs_merchant_keyid": ["Key ID"],
  "dynamic_info.cybs_merchant_secretkey": ["Secret Key"],
  "dynamic_info.local_debit_base_rate": ["Base Rate (Local Debit)"],
  "dynamic_info.local_debit_markup_rate": ["Markup Rate (Local Debit)"],
  "dynamic_info.local_credit_base_rate": ["Base Rate (Local Credit)"],
  "dynamic_info.local_credit_markup_rate": ["Markup Rate (Local Credit)"],
  "dynamic_info.foreign_base_rate": ["Base Rate (Foreign Card)"],
  "dynamic_info.foreign_markup_rate": ["Markup Rate (Foreign Card)"],
  "dynamic_info.my_debit_base_rate": ["Base Rate (MyDebit Card)"],
  "dynamic_info.my_debit_markup_rate": ["Markup Rate (MyDebit Card)"],
  "gw2_proxy_route.gw2_params.mcptid": ["MCPTID"],
  "gw2_proxy_route.gw2_params.etype": ["EType"],
  "gw2_proxy_route.route_enabled": ["Enable Route GW2"],
};

const normalizeFieldLabel = (label) =>
  String(label || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const buildRouteComparisonLookup = (route, original) => {
  if (!route || !original) return null;
  const beforeValues = comparableRouteValues(original);
  const afterValues = comparableRouteValues(route);
  const lookup = new Map();

  Array.from(new Set([...Object.keys(beforeValues), ...Object.keys(afterValues)]))
    .filter(
      (key) =>
        normalizeComparisonValue(beforeValues[key]) !==
        normalizeComparisonValue(afterValues[key])
    )
    .forEach((key) => {
      const comparison = { before: beforeValues[key], after: afterValues[key] };
      const keyParts = key.split(".");
      const leafKey = keyParts[keyParts.length - 1];
      const unprefixedLeafKey = leafKey.replace(
        /^(host_fiuu|cybs|grabpay|touchngo|revpay)_/,
        ""
      );
      const labels = [
        key,
        routeFieldLabel(key),
        ...(keyParts.length <= 2 ? [leafKey, unprefixedLeafKey] : []),
        ...(routeComparisonAliases[key] || []),
      ];
      labels.forEach((label) => lookup.set(normalizeFieldLabel(label), comparison));
    });

  return lookup;
};

const RouteComparisonContext = React.createContext(null);

const TextField = ({ comparisonKey, maskComparison = false, InputProps, sx, ...props }) => {
  const comparisonLookup = React.useContext(RouteComparisonContext);
  const comparison = comparisonLookup?.get(
    normalizeFieldLabel(comparisonKey || props.label)
  );

  if (!comparison) {
    return <MuiTextField InputProps={InputProps} sx={sx} {...props} />;
  }
  const comparisonBefore = maskComparison && comparison.before
    ? "••••••••"
    : comparison.before;

  return (
    <MuiTextField
      {...props}
      InputProps={{
        ...InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <Stack direction="row" spacing={1} alignItems="center">
              {InputProps?.startAdornment}
              <Box
                component="span"
                sx={{ color: "text.secondary", textDecoration: "line-through" }}
              >
                {displayComparisonValue(comparisonBefore)}
              </Box>
              <ArrowForward sx={{ color: "text.secondary", fontSize: 18 }} />
            </Stack>
          </InputAdornment>
        ),
      }}
      sx={{
        ...sx,
        "& .MuiInputBase-root": {
          bgcolor: "#e8f5e9",
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: "#1b5e20",
          fontWeight: 600,
        },
      }}
    />
  );
};

const FormControlLabel = ({ comparisonKey, control, label, ...props }) => {
  const comparisonLookup = React.useContext(RouteComparisonContext);
  const comparison = comparisonLookup?.get(
    normalizeFieldLabel(comparisonKey || label)
  );

  if (!comparison) {
    return <MuiFormControlLabel control={control} label={label} {...props} />;
  }

  return (
    <MuiFormControlLabel
      {...props}
      control={control}
      label={
        <Stack spacing={0.5}>
          <Box component="span">{label}</Box>
          <InlineComparison before={comparison.before} after={comparison.after} />
        </Stack>
      }
    />
  );
};

const RouteDetail = ({ comparisonEnabled = false, comparisonOriginal, showActionNotice = false, ...route }) => {
  let paymentRoute = route.payment_charges_pending_payment_mapping
    ? route.payment_charges_pending_payment_mapping
    : route.payment_charges_payment_mapping;
  let dynamicInfo = route.dynamic_info;
  const originalDynamicInfo = comparisonOriginal?.dynamic_info || {};
  const hasCurrentOrOriginalDynamicValue = (field) =>
    [dynamicInfo?.[field], originalDynamicInfo?.[field]].some(
      (value) => value !== null && value !== undefined && value !== ""
    );

  let paymentHostName = route?.payment_host?.name?.toLocaleLowerCase();
  let paymentBrandName = route?.payment_brand?.name?.toLocaleLowerCase();
  const isShopeePayBrand = paymentBrandName === PAYMENT_HOST_LIST.SHOPEEPAY.toLowerCase();
  const isShopeePaySGOrTHHost = [PAYMENT_HOST_LIST.SHOPEEPAY_SG?.toLowerCase(), PAYMENT_HOST_LIST.SHOPEEPAY_TH?.toLowerCase()].includes(paymentHostName?.toLowerCase());

  const routeAction = String(route?.action || "").toLowerCase();
  const isAdded = routeAction === "add";
  const isRemoved = routeAction === "remove";
  const isUpdated = routeAction === "update";
  const isActive = route?.is_active !== false;
  const isSuspended = routeAction === "suspend" || route?.is_suspended === true;
  const statusText = isSuspended ? "Suspended" : (isActive ? "Active" : "Inactive");
  const statusColor = statusText === "Active" ? "success" : statusText === "Suspended" ? "primary" : "error";
  const actionLabelColor = isAdded
    ? "info"
    : isRemoved
      ? "error"
      : isUpdated
        ? "success"
        : "primary";
  const actionLabelVariant = (isAdded || isRemoved || isUpdated) ? "filled" : "ghost";
  const comparisonLookup = comparisonEnabled
    ? buildRouteComparisonLookup(route, comparisonOriginal)
    : null;
  const renderRouteValue = (before, after) =>
    comparisonEnabled ? (
      <InlineComparison before={before} after={after} />
    ) : (
      displayComparisonValue(after)
    );

    return (
      <RouteComparisonContext.Provider value={comparisonLookup}>
      <Card
        sx={{
          p: 3,
          my: 2,
          ...(isAdded && {
            bgcolor: "#f8fbff",
            border: "1px solid",
            borderColor: "info.light",
            boxShadow: "0 1px 4px rgba(2, 136, 209, 0.12)",
          }),
          ...(isRemoved && {
            bgcolor: "#fff4f4",
            border: "1px solid",
            borderColor: "error.light",
          }),
          ...(isSuspended && {
            bgcolor: "grey.100",
            border: "1px solid",
            borderColor: "grey.300",
          }),
        }}
      >
        <Grid
          container
          direction={"row"}
          spacing={{ xs: 2, sm: 2, md: 2 }}
          sx={{ mt: 5 }}
        >
          <Grid
            container
            direction={"row"}
            sx={{
              height: "100%",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", mb: 2 }}>
              {route.action ? (
                <Label variant={actionLabelVariant} color={actionLabelColor}>
                  {route.action}
                </Label>
              ) : (
                <Label variant="ghost" color={statusColor}>{statusText}</Label>
              )}
              {showActionNotice && isRemoved && (
                <Typography variant="body2" color="error.main" fontWeight={600}>
                  This route will be removed
                </Typography>
              )}
              {showActionNotice && isSuspended && (
                <Typography variant="body2" color="error.main" fontWeight={600}>
                  This route will be suspended
                </Typography>
              )}
            </Stack>
            <Grid item xs={4}>
              <Typography component="div" variant="subtitle1" textAlign={"center"}>
                Payment Method: {renderRouteValue(
                  comparisonOriginal?.payment_method?.name,
                  route.payment_method?.name
                )}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign={"center"}>
              <Typography component="div" variant="subtitle1">
                Payment Brand: {renderRouteValue(
                  comparisonOriginal?.payment_brand?.name,
                  route.payment_brand?.name
                )}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign={"center"}>
              <Typography component="div" variant="subtitle1">
                Payment Host: {renderRouteValue(
                  comparisonOriginal?.payment_host?.name,
                  route.payment_host?.name
                )}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            height: "100%",
            p: 3,
          }}
        >
          <Grid
            container
            column={6}
            spacing={2}
            rowSpacing={4}
            sx={{
              height: "100%",
              p: 3,
            }}
          >
            <Grid item xs={12}>
              {route?.currencies &&
                <Grid item xs={3}>
                  <Typography sx={{ color: "#637381", fontSize: 12 }} >
                    Currency
                  </Typography>
                  {(() => {
                    const currencies = route?.currencies || [];
                    const currencyComparison = comparisonLookup?.get(
                      normalizeFieldLabel("Currency")
                    );
                    if (currencyComparison) {
                      return (
                        <Box
                          sx={{
                            minHeight: 38,
                            px: 1.5,
                            py: 0.75,
                            border: "1px solid #c4cdd5",
                            borderRadius: 1,
                            bgcolor: "#e8f5e9",
                          }}
                        >
                          <InlineComparison
                            before={currencyComparison.before}
                            after={currencyComparison.after}
                          />
                        </Box>
                      );
                    }
                    return (
                      <Select
                        getOptionLabel={option => option.currency}
                        getOptionValue={option => option.id}
                        closeMenuOnSelect={false}
                        isMulti
                        isDisabled
                        options={currencies}
                        value={currencies}
                        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                        placeholder="No currency selected"
                      />
                    );
                  })()}
                </Grid>
              }
            </Grid>

            <Grid item xs={12}>
              {route?.currencies &&
                <Grid container spacing={2}>
                  {(() => {
                    const currencies = route?.currencies || [];
                    return currencies.map((currency, index) => {
                      return (
                        <Grid item xs={3} key={index}>
                          <TextField
                            fullWidth
                            comparisonKey={`currency ${currency.currency} min amount`}
                            label="Min Amount"
                            disabled
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">{currency.currency}</InputAdornment>
                              ),
                            }}
                            value={
                              currency.min_amount != null
                                ? currency.min_amount
                                : "N/A"
                            }
                          />
                        </Grid>
                      );
                    })
                  })()}
                </Grid>
              }
            </Grid>
            {!showAllinpayDynamicField(paymentHostName) && (
              <>
                <Grid item xs={3}>
                  {" "}
                  <TextField
                    comparisonKey="charges.base_rate"
                    label={isShopeePayBrand && isShopeePaySGOrTHHost ? "Base Rate (Wallet)" : "Base Rate"}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    value={
                      paymentRoute[0]?.base_rate
                        ? toNumberInputString(paymentRoute[0].base_rate)
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  {" "}
                  <TextField
                    comparisonKey="charges.markup_rate"
                    label={isShopeePayBrand && isShopeePaySGOrTHHost ? "Markup Rate (Wallet)" : "Markup Rate"}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    value={
                      paymentRoute[0]?.markup_rate
                        ? toNumberInputString(paymentRoute[0].markup_rate)
                        : "N/A"
                    }
                  />
                </Grid>
              </>
            )}
            {dynamicInfo && showAllinpayDynamicField(paymentHostName) && (
              <>
                {[
                  ["base_rate_local", "Base Rate (Local)"],
                  ["markup_rate_local", "Markup Rate (Local)"],
                  ["base_rate_foreign", "Base Rate (Foreign)"],
                  ["markup_rate_foreign", "Markup Rate (Foreign)"],
                ].map(([name, label]) => (
                  <Grid item xs={3} key={name}>
                    <TextField
                      comparisonKey={`dynamic_info.${name}`}
                      label={label}
                      disabled
                      InputProps={{
                        startAdornment: <InputAdornment position="start">%</InputAdornment>,
                      }}
                      value={dynamicInfo[name] !== null && dynamicInfo[name] !== undefined && dynamicInfo[name] !== ""
                        ? toNumberInputString(dynamicInfo[name])
                        : "N/A"}
                    />
                  </Grid>
                ))}
              </>
            )}

            {
              isShopeePayBrand && dynamicInfo && isShopeePaySGOrTHHost && (
                <>
                  <Grid item xs={3}>
                    <TextField
                      comparisonKey="dynamic_info.merchant_ext_id"
                      label="Merchant Ext ID"
                      disabled
                      value={dynamicInfo.merchant_ext_id ? dynamicInfo.merchant_ext_id : "N/A"}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      comparisonKey="dynamic_info.store_ext_id"
                      label="Store Ext ID"
                      disabled
                      value={dynamicInfo.store_ext_id ? dynamicInfo.store_ext_id : "N/A"}
                    />
                  </Grid>
                  {(
                    hasCurrentOrOriginalDynamicValue("credit_base_rate") ||
                    hasCurrentOrOriginalDynamicValue("credit_markup_rate")
                  ) && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.credit_base_rate"
                          label="Base Rate (Debit/Credit Card)"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">%</InputAdornment>
                            ),
                          }}
                          value={
                            dynamicInfo.credit_base_rate != null
                              ? toNumberInputString(dynamicInfo.credit_base_rate)
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.credit_markup_rate"
                          label="Markup Rate (Debit/Credit Card)"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">%</InputAdornment>
                            ),
                          }}
                          value={
                            dynamicInfo.credit_markup_rate != null
                              ? toNumberInputString(dynamicInfo.credit_markup_rate)
                              : "N/A"
                          }
                        />
                      </Grid>
                    </>
                  )}
                  {(
                    hasCurrentOrOriginalDynamicValue("spaylater_base_rate") ||
                    hasCurrentOrOriginalDynamicValue("spaylater_markup_rate")
                  ) && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.spaylater_base_rate"
                          label="Base Rate (SPayLater)"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">%</InputAdornment>
                            ),
                          }}
                          value={
                            dynamicInfo.spaylater_base_rate != null
                              ? toNumberInputString(dynamicInfo.spaylater_base_rate)
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.spaylater_markup_rate"
                          label="Markup Rate (SPayLater)"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">%</InputAdornment>
                            ),
                          }}
                          value={
                            dynamicInfo.spaylater_markup_rate != null
                              ? toNumberInputString(dynamicInfo.spaylater_markup_rate)
                              : "N/A"
                          }
                        />
                      </Grid>
                    </>
                  )}
                </>
              )
            }
            
            {
              (paymentHostName?.toLowerCase() === PAYMENT_HOST_LIST.FIUU?.toLowerCase() || paymentHostName?.toLowerCase() === PAYMENT_HOST_LIST.FIUU_DIRECT_PF?.toLowerCase()) && (
                <>
                  <Grid item xs={3}>
                    {" "}
                    <TextField
                      comparisonKey="charges.base_rate_foreign"
                      label="Base Rate (Foreign Card)"
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      }}
                      value={
                        paymentRoute[0]?.base_rate_foreign
                          ? toNumberInputString(paymentRoute[0].base_rate_foreign)
                          : "N/A"
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    {" "}
                    <TextField
                      comparisonKey="charges.markup_rate_foreign"
                      label="Markup Rate (Foreign Card)"
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      }}
                      value={
                        paymentRoute[0]?.markup_rate_foreign
                          ? toNumberInputString(paymentRoute[0].markup_rate_foreign)
                          : "N/A"
                      }
                    />
                  </Grid>
                </>
              )
            }
            {hasCurrentOrOriginalDynamicValue("paynow_descriptor_name") &&
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.paynow_descriptor_name"
                  label="PayNow Descriptor"
                  disabled
                  value={dynamicInfo?.paynow_descriptor_name}
                />
              </Grid>
            }

            {hasCurrentOrOriginalDynamicValue("ref_2") &&
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.ref_2"
                  label="PayNow Suffix"
                  disabled
                  value={dynamicInfo?.ref_2}
                />
              </Grid>
            }

            {hasCurrentOrOriginalDynamicValue("payee") &&
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.payee"
                  label="Payee"
                  disabled
                  value={dynamicInfo?.payee}
                />
              </Grid>
            }

            {hasCurrentOrOriginalDynamicValue("sub_mid") &&
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.sub_mid"
                  label="Sub MID"
                  disabled
                  value={dynamicInfo?.sub_mid}
                />
              </Grid>
            }

            {hasCurrentOrOriginalDynamicValue("line_pay_merchant_id") &&
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.line_pay_merchant_id"
                  label="LINE Pay Merchant ID"
                  disabled
                  value={dynamicInfo?.line_pay_merchant_id}
                />
              </Grid>
            }

            {dynamicInfo && paymentHostName?.toLowerCase() === PAYMENT_HOST_LIST.ATOME && (
              <>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.username"
                    label="Atome username"
                    disabled
                    value={
                      dynamicInfo.username != null && dynamicInfo.username !== ""
                        ? dynamicInfo.username
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.password"
                    label="Atome password"
                    disabled
                    value={
                      dynamicInfo.password != null && dynamicInfo.password !== ""
                        ? dynamicInfo.password
                        : "N/A"
                    }
                  />
                </Grid>
              </>
            )}

            {dynamicInfo && paymentHostName?.toLowerCase() === PAYMENT_HOST_LIST.WORLDPAY?.toLowerCase() && (
              <>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.pf_id"
                    label="Sub Merchant pf id"
                    disabled
                    value={dynamicInfo.pf_id ? dynamicInfo.pf_id : "N/A"}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.sub_name"
                    label="Sub Merchant Name"
                    disabled
                    value={dynamicInfo.sub_name ? dynamicInfo.sub_name : "N/A"}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.sub_id"
                    label="Sub Merchant ID"
                    disabled
                    value={dynamicInfo.sub_id ? dynamicInfo.sub_id : "N/A"}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.sub_street"
                    label="Sub Merchant Street"
                    disabled
                    value={
                      dynamicInfo.sub_street ? dynamicInfo.sub_street : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.sub_city"
                    label="Sub Merchant City"
                    disabled
                    value={dynamicInfo.sub_city ? dynamicInfo.sub_city : "N/A"}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.sub_country_code"
                    label="Sub Merchant Country Code"
                    disabled
                    value={
                      dynamicInfo.sub_country_code
                        ? SUB_MERCHANT_COUNTRY_CODE.find(item => item.id.toString() === dynamicInfo.sub_country_code)?.name || "N/A"
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.sub_postal_code"
                    label="Sub Merchant Postal Code"
                    disabled
                    value={
                      dynamicInfo.sub_postal_code
                        ? dynamicInfo.sub_postal_code
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.mcc"
                    label="MCC"
                    disabled
                    value={dynamicInfo.mcc ? dynamicInfo.mcc : "N/A"}
                  />
                </Grid>
                {dynamicInfo.vis_dynamic_info && (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.vis_dynamic_info.partner_merchant_reference_id"
                        label="Partner Merchant Reference id"
                        disabled
                        value={
                          dynamicInfo.vis_dynamic_info
                            .partner_merchant_reference_id
                            ? dynamicInfo.vis_dynamic_info
                              .partner_merchant_reference_id
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.currency"
                        label="Currency"
                        disabled
                        value={
                          dynamicInfo.currency
                            ? dynamicInfo.currency.length == 0
                              ? "N/A"
                              : dynamicInfo.currency
                                .map(({ id, currency }) => `${currency}`)
                                .join(", ")
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <FormControlLabel
                        comparisonKey="dynamic_info.enabled_3ds"
                        sx={{ mx: 2 }}
                        disabled
                        type="checkbox"
                        checked={dynamicInfo.enabled_3ds !== undefined}
                        control={<Checkbox />}
                        label="Enable 3DS"
                      />
                    </Grid>
                  </>
                )}
                {route.default_bin_routing === false &&
                  dynamicInfo.supercede_info && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.supercede_info.min_card_range"
                          label="Min Card Range"
                          disabled
                          value={
                            dynamicInfo.supercede_info.min_card_range
                              ? dynamicInfo.supercede_info.min_card_range
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.supercede_info.max_card_range"
                          label="Max Card Range"
                          disabled
                          value={
                            dynamicInfo.supercede_info.max_card_range
                              ? dynamicInfo.supercede_info.max_card_range
                              : "N/A"
                          }
                        />
                      </Grid>
                    </>
                  )}
              </>
            )}
            {dynamicInfo &&
              (paymentHostName.includes(PAYMENT_HOST_LIST.GRABPAY) ||
                paymentHostName.includes(PAYMENT_HOST_LIST.DUITNOW)) && !paymentHostName.includes(PAYMENT_HOST_LIST.GRABPAY_GW2) && (
                <>
                  <Grid item xs={3}>
                    <TextField
                      comparisonKey="dynamic_info.grabpay_merchant_id"
                      label="GrabPay Store ID"
                      disabled
                      value={
                        dynamicInfo.grabpay_merchant_id
                          ? dynamicInfo.grabpay_merchant_id
                          : "N/A"
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      comparisonKey="dynamic_info.grabpay_terminal_id"
                      label="GrabPay Terminal ID"
                      disabled
                      value={
                        dynamicInfo.grabpay_terminal_id
                          ? dynamicInfo.grabpay_terminal_id
                          : "N/A"
                      }
                    />
                  </Grid>
                  {!paymentHostName.includes(PAYMENT_HOST_LIST.GRABPAY_V1) && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.payment_expiry_time"
                          label="Payment Expiry Time (Min)"
                          disabled
                          value={
                            dynamicInfo.payment_expiry_time
                              ? dynamicInfo.payment_expiry_time
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.min_amt_postpaid"
                          label="Min Amount (Postpaid)"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }}
                          value={
                            dynamicInfo.min_amt_postpaid
                              ? toNumberInputString(dynamicInfo.min_amt_postpaid)
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.min_amt_installment_4"
                          label="Min Amount (Installment)"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          }}
                          value={
                            dynamicInfo.min_amt_installment_4
                              ? toNumberInputString(dynamicInfo.min_amt_installment_4)
                              : "N/A"
                          }
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}
              {dynamicInfo &&
                paymentHostName.includes(PAYMENT_HOST_LIST.TOUCHNGO) && (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.touchngo_expiry_time"
                        label="Expiry Time (Min)"
                        disabled
                        value={
                          dynamicInfo.touchngo_expiry_time
                            ? dynamicInfo.touchngo_expiry_time
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.touchngo_merchant_id"

                        label="TouchNGo Merchant ID"
                        disabled
                        value={
                          dynamicInfo.touchngo_merchant_id
                            ? dynamicInfo.touchngo_merchant_id
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.touchngo_client_id"
                        label="TouchNGo Client ID"
                        disabled
                        value={
                          dynamicInfo.touchngo_client_id
                            ? dynamicInfo.touchngo_client_id
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.touchngo_client_secret"
                        label="TouchNGo Client Secret"
                        disabled
                        value={
                          dynamicInfo.touchngo_client_secret
                            ? dynamicInfo.touchngo_client_secret
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="dynamic_info.is_autodebit_enabled"
                        label="AutoDebit Enabled"
                        disabled
                        value={dynamicInfo.is_autodebit_enabled ? "Yes" : "No"}
                      />
                    </Grid>
                    {dynamicInfo.is_autodebit_enabled && (
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.autodebit_amount"
                          label="AutoDebit Amount"
                          disabled
                          value={
                            dynamicInfo.autodebit_amount
                              ? dynamicInfo.autodebit_amount
                              : "N/A"
                          }
                        />
                      </Grid>
                    )}

                  </>
                )}

              {dynamicInfo &&
                showDashDynamicField(paymentHostName) && (
                  <>
                    <Grid item xs={2}>
                      <TextField
                        comparisonKey="dynamic_info.counter_id"
                        label="Counter ID"
                        disabled
                        value={
                          dynamicInfo.counter_id
                            ? dynamicInfo.counter_id
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        comparisonKey="dynamic_info.counter_msisdn"
                        label="Counter MSISDN"
                        disabled
                        value={
                          dynamicInfo.counter_msisdn
                            ? dynamicInfo.counter_msisdn
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        comparisonKey="dynamic_info.counter_pin"
                        label="Counter PIN"
                        disabled
                        value={
                          dynamicInfo.counter_pin
                            ? dynamicInfo.counter_pin
                            : "N/A"
                        }
                      />
                    </Grid>
                  </>
                )}
              {dynamicInfo &&
                showAliPayDynamicField(paymentHostName) && (
                  <Grid item xs={2}>
                    <TextField
                      comparisonKey="dynamic_info.store_id"
                      label="Store ID"
                      disabled
                      value={dynamicInfo.store_id ? dynamicInfo.store_id : "N/A"}
                    />
                  </Grid>
                )}
              {dynamicInfo &&
                showWeChatDynamicField(paymentHostName) && (
                  <Grid item xs={3}>
                    <TextField
                      comparisonKey="dynamic_info.partner_id"
                      label="Partner ID"
                      disabled
                      value={dynamicInfo.partner_id ? dynamicInfo.partner_id : "N/A"}
                    />
                  </Grid>
                )}

            {route?.mpi_type != null && route.payment_method?.name?.toLowerCase() === "card" && !showCybersourceDynamicField(paymentHostName) &&
              <Grid item xs={4}>
                <TextField
                  comparisonKey="mpi_type"
                  fullWidth
                  label="MPI Type"
                  disabled
                  value={MPI_TYPE[route.mpi_type]}
                />
              </Grid>
            }

            {+route?.mpi_type !== 0 && route?.mpi_id && 
              <Grid item xs={4}>
                <TextField
                  comparisonKey="mpi_id"
                  fullWidth
                  label="MPI (Acquirer Bin - Acquirer Merchant ID)"
                  disabled
                  value={`${route.mpi_id.acquirer_bin} - ${route.mpi_id.acquirer_merchant_id}`}
                />
              </Grid>
            }
          </Grid>


          {dynamicInfo &&
            (paymentHostName.toLowerCase().includes(PAYMENT_HOST_LIST.NOMU)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.processing_account_id"
                    label="Processing Account Id"
                    disabled
                    value={
                      dynamicInfo.processing_account_id
                        ? dynamicInfo.processing_account_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    comparisonKey="default_bin_routing"
                    sx={{ mx: 2 }}
                    disabled
                    type="checkbox"
                    checked={route.default_bin_routing}
                    control={<Checkbox />}
                    label="Default Bin Routing"
                  />
                </Grid>

                {route.default_bin_routing === false &&
                  dynamicInfo.preset_info && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.preset_info.min_card_range"
                          label="Min Card Range"
                          disabled
                          value={
                            dynamicInfo.preset_info.min_card_range
                              ? dynamicInfo.preset_info.min_card_range
                              : "N/A"
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          comparisonKey="dynamic_info.preset_info.max_card_range"
                          label="Max Card Range"
                          disabled
                          value={
                            dynamicInfo.preset_info.max_card_range
                              ? dynamicInfo.preset_info.max_card_range
                              : "N/A"
                          }
                        />
                      </Grid>
                    </>
                  )}
              </Grid>
            )
          }

          {dynamicInfo && showAllinpayDynamicField(paymentHostName) && (
            <Grid
              container
              column={6}
              spacing={2}
              rowSpacing={4}
              sx={{ height: "100%", p: 3 }}
            >
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.merchant_id"
                  label="Merchant ID"
                  disabled
                  value={dynamicInfo.merchant_id || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.terminal_id"
                  label="Terminal ID"
                  disabled
                  value={dynamicInfo.terminal_id || "N/A"}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  comparisonKey="dynamic_info.partner_id"
                  label="Partner ID"
                  disabled
                  value={dynamicInfo.partner_id || "N/A"}
                />
              </Grid>
            </Grid>
          )}

          {dynamicInfo &&
            (paymentHostName.toLowerCase().includes(PAYMENT_HOST_LIST.RAZER)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.secret_key"
                    label="Secret Key"
                    disabled
                    value={
                      dynamicInfo.secret_key
                        ? dynamicInfo.secret_key
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.verification_key"
                    label="Verification Key"
                    disabled
                    value={
                      dynamicInfo.verification_key
                        ? dynamicInfo.verification_key
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.merchant_id"
                    label="Merchant ID"
                    disabled
                    value={
                      dynamicInfo.merchant_id
                        ? dynamicInfo.merchant_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.channel"
                    label="Channel"
                    disabled
                    value={
                      dynamicInfo.channel
                        ? dynamicInfo.channel
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            )
          }

          {dynamicInfo &&
            showFiuuFixedFeeDynamicField(paymentHostName) && (
              <Grid container column={8} spacing={2} rowSpacing={4} sx={{ height: "100%", p: 3 }}>
                {[
                  ["base_fix_fee_local", "Base Fixed Fee (Local)"],
                  ["markup_fix_fee_local", "Markup Fixed Fee (Local)"],
                  ["base_fix_fee_foreign", "Base Fixed Fee (Foreign)"],
                  ["markup_fix_fee_foreign", "Markup Fixed Fee (Foreign)"],
                ].map(([name, label]) => (
                  <Grid item xs={3} key={name}>
                    <TextField
                      comparisonKey={`dynamic_info.${name}`}
                      label={label}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      value={dynamicInfo[name] !== null && dynamicInfo[name] !== undefined && dynamicInfo[name] !== ""
                        ? toNumberInputString(dynamicInfo[name])
                        : "N/A"}
                    />
                  </Grid>
                ))}
              </Grid>
            )
          }

          {dynamicInfo &&
            (showFiuuDynamicField(paymentHostName)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.secret_key"
                    label="Secret Key"
                    disabled
                    value={
                      dynamicInfo.secret_key
                        ? dynamicInfo.secret_key
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.merchant_id"
                    label="Merchant ID"
                    disabled
                    value={
                      dynamicInfo.merchant_id
                        ? dynamicInfo.merchant_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.terminal_id"
                    label="Terminal ID"
                    disabled
                    value={
                      dynamicInfo.terminal_id
                        ? dynamicInfo.terminal_id
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            )
          }
          
          {dynamicInfo &&
            (paymentHostName.toLowerCase().includes(PAYMENT_HOST_LIST.SHOPBACK)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.access_key_id"
                    label="Access Key ID"
                    disabled
                    value={
                      dynamicInfo.access_key_id
                        ? dynamicInfo.access_key_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.access_key_secret"
                    label="Access Key Secret"
                    disabled
                    value={
                      dynamicInfo.access_key_secret
                        ? dynamicInfo.access_key_secret
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.pos_id"
                    label="POS ID"
                    disabled
                    value={
                      dynamicInfo.pos_id
                        ? dynamicInfo.pos_id
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            )
          }

          {dynamicInfo &&
            (paymentHostName.toLowerCase().includes(PAYMENT_HOST_LIST.NETS)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.secret_id"
                    label="SecretID"
                    disabled
                    value={
                      dynamicInfo.secret_id
                        ? dynamicInfo.secret_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.key_id"
                    label="KeyID"
                    disabled
                    value={
                      dynamicInfo.key_id
                        ? dynamicInfo.key_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.mid"
                    label="MID"
                    disabled
                    value={
                      dynamicInfo.mid
                        ? dynamicInfo.mid
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.tid"
                    label="TID"
                    disabled
                    value={
                      dynamicInfo.tid
                        ? dynamicInfo.tid
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.institution_code"
                    label="InstitutionCode"
                    disabled
                    value={
                      dynamicInfo.institution_code
                        ? dynamicInfo.institution_code
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.condition_code"
                    label="Condition Code"
                    disabled
                    value={
                      dynamicInfo.condition_code
                        ? dynamicInfo.condition_code
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            )
          }

          {dynamicInfo &&
            (showHostFiuuDynamicField(paymentHostName)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.host_fiuu_mid"
                    label="MID"
                    disabled
                    value={
                      dynamicInfo.host_fiuu_mid
                        ? dynamicInfo.host_fiuu_mid
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.host_fiuu_verify_key"
                    label="Verify Key"
                    disabled
                    value={
                      dynamicInfo.host_fiuu_verify_key
                        ? dynamicInfo.host_fiuu_verify_key
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.host_fiuu_secret_key"
                    label="Secret Key"
                    disabled
                    value={
                      dynamicInfo.host_fiuu_secret_key
                        ? dynamicInfo.host_fiuu_secret_key
                        : "N/A"
                    }
                  />
                </Grid>
                {/* <Grid item xs={3}>
                  <TextField
                    label="Email"
                    disabled
                    value={
                      dynamicInfo.host_fiuu_email
                        ? dynamicInfo.host_fiuu_email
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Password"
                    disabled
                    value={
                      dynamicInfo.host_fiuu_password
                        ? dynamicInfo.host_fiuu_password
                        : "N/A"
                    }
                  />
                </Grid> */}
              </Grid>
            )
          }

          {dynamicInfo &&
            (showCybersourceDynamicField(paymentHostName)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.cybs_merchant_id"
                    label="Merchant ID"
                    disabled
                    value={
                      dynamicInfo.cybs_merchant_id
                        ? dynamicInfo.cybs_merchant_id
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.cybs_merchant_keyid"
                    label="Key ID"
                    disabled
                    value={
                      dynamicInfo.cybs_merchant_keyid
                        ? dynamicInfo.cybs_merchant_keyid
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.cybs_merchant_secretkey"
                    label="Secret Key"
                    disabled
                    value={
                      dynamicInfo.cybs_merchant_secretkey
                        ? dynamicInfo.cybs_merchant_secretkey
                        : "N/A"
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.mpi_type"
                    label="MPI Type"
                    disabled
                    value={
                      dynamicInfo.mpi_type !== undefined && dynamicInfo.mpi_type !== null
                        ? MPI_TYPE[dynamicInfo.mpi_type] || dynamicInfo.mpi_type
                        : "N/A"
                    }
                  />
                </Grid>
                {dynamicInfo.ipp_enabled && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Installment Payment Plan (IPP)
                      </Typography>
                    </Grid>
                    {dynamicInfo.ipp_plans && dynamicInfo.ipp_plans.length > 0 && (
                      dynamicInfo.ipp_plans.map((plan, planIndex) => (
                        <Grid item xs={12} key={`${plan.bank}-${plan.months}-${planIndex}`}>
                          <Card sx={{ p: 2, border: '1px solid #e0e0e0', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                              {plan.bank || "N/A"} ({plan.months || "N/A"} months)
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  comparisonKey={`dynamic_info.ipp_plans.${planIndex}.ipp_merchant_id`}
                                  label="Merchant ID"
                                  disabled
                                  fullWidth
                                  size="small"
                                  value={plan.ipp_merchant_id || plan.mid || "N/A"}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  comparisonKey={`dynamic_info.ipp_plans.${planIndex}.ipp_merchant_keyid`}
                                  label="Merchant Key ID"
                                  disabled
                                  fullWidth
                                  size="small"
                                  value={plan.ipp_merchant_keyid || plan.username || "N/A"}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  comparisonKey={`dynamic_info.ipp_plans.${planIndex}.ipp_merchant_secretkey`}
                                  maskComparison
                                  label="Merchant Secret Key"
                                  disabled
                                  fullWidth
                                  size="small"
                                  type="password"
                                  value={(plan.ipp_merchant_secretkey || plan.password) ? "••••••••" : "N/A"}
                                />
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))
                    )}
                  </>
                )}
              </Grid>
            )
          }

          {dynamicInfo &&
            (paymentHostName.toLowerCase().includes(PAYMENT_HOST_LIST.REVPAY)) && (
              <Grid
                container
                column={6}
                spacing={2}
                rowSpacing={4}
                sx={{
                  height: "100%",
                  p: 3,
                }}
              >
                <Grid item xs={3}>
                  <TextField
                    comparisonKey="dynamic_info.revpay_merchant_id"
                    label="RevPay Merchant ID"
                    disabled
                    value={
                      dynamicInfo.revpay_merchant_id
                        ? dynamicInfo.revpay_merchant_id
                        : "N/A"
                    }
                  />
                </Grid>
              </Grid>
            )
          }

          {dynamicInfo && (paymentHostName.includes("paydee") || paymentHostName.includes("bsn")) && (
            <Grid item xs={12} sx={{ position: 'relative' }}>

              <Box sx={{ 
                position: { md: 'absolute' }, 
                top: { md: -240 },
                left: { md: '28%' },
                width: { md: '60%' },
                display: 'flex',
                gap: 2,
                zIndex: 10,
                pl: 2.5
              }}>
                <TextField 
                  comparisonKey="dynamic_info.bank_mid"
                  fullWidth 
                  label="Bank MID" 
                  disabled 
                  value={dynamicInfo.bank_mid || "N/A"} 
                  size="small"
                />
                <TextField 
                  comparisonKey="dynamic_info.bank_tid"
                  fullWidth 
                  label="Bank TID" 
                  disabled 
                  value={dynamicInfo.bank_tid || "N/A"} 
                  size="small"
                />
              </Box>

              <Grid container spacing={2} sx={{ pl: 2.5 }}>

                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.local_debit_base_rate" fullWidth label="Base Rate (Local Debit)" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.local_debit_base_rate || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.local_debit_markup_rate" fullWidth label="Markup Rate (Local Debit)" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.local_debit_markup_rate || "N/A"} />
                </Grid>
                <Grid item xs={6} />

                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.local_credit_base_rate" fullWidth label="Base Rate (Local Credit)" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.local_credit_base_rate || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.local_credit_markup_rate" fullWidth label="Markup Rate (Local Credit)" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.local_credit_markup_rate || "N/A"} />
                </Grid>
                <Grid item xs={6} />

                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.foreign_base_rate" fullWidth label="Base Rate (Foreign Card)" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.foreign_base_rate || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.foreign_markup_rate" fullWidth label="Markup Rate (Foreign Card)" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.foreign_markup_rate || "N/A"} />
                </Grid>
                <Grid item xs={6} />

                {paymentHostName.includes("bsn") && (
                  <>
                    <Grid item xs={3} sx={{ mt: 2 }}>
                      <TextField comparisonKey="dynamic_info.my_debit_base_rate" fullWidth label="Base Rate (MyDebit Card)" disabled
                        InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                        value={dynamicInfo.my_debit_base_rate || "N/A"} />
                    </Grid>
                    <Grid item xs={3} sx={{ mt: 2 }}>
                      <TextField comparisonKey="dynamic_info.my_debit_markup_rate" fullWidth label="Markup Rate (MyDebit Card)" disabled
                        InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                        value={dynamicInfo.my_debit_markup_rate || "N/A"} />
                    </Grid>
                    <Grid item xs={6} />
                  </>
                )}

                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.whichever_higher_mdr_cents" fullWidth label="Minimum MDR" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">¢</InputAdornment> }}
                    value={dynamicInfo.whichever_higher_mdr_cents || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.micro_transaction_amount" fullWidth label="Micro Transaction Amount" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">¢</InputAdornment> }}
                    value={dynamicInfo.micro_transaction_amount || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.mdr_less_than_micro_transaction_amount_cents" fullWidth label="Micro Transaction MDR" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">¢</InputAdornment> }}
                    value={dynamicInfo.mdr_less_than_micro_transaction_amount_cents || "N/A"} />
                </Grid>
              </Grid>
            </Grid>
          )}

          {dynamicInfo && (paymentHostName.includes(PAYMENT_HOST_LIST.SHOPEEPAY_MY)) && (
            <Grid item xs={12} sx={{ position: 'relative' }}>

              <Box sx={{
                position: { md: 'absolute' },
                top: { md: -240 },
                left: { md: '28%' },
                width: { md: '60%' },
                display: 'flex',
                gap: 2,
                zIndex: 10,
                pl: 2.5
              }}>
                <TextField
                  comparisonKey="dynamic_info.my_merchant_ext_id"
                  fullWidth
                  label="MY Merchant Ext ID"
                  disabled
                  value={dynamicInfo.my_merchant_ext_id || "N/A"}
                  size="small"
                />
                <TextField
                  comparisonKey="dynamic_info.my_store_ext_id"
                  fullWidth
                  label="MY Store Ext ID"
                  disabled
                  value={dynamicInfo.my_store_ext_id || "N/A"}
                  size="small"
                />
              </Box>

              <Grid container spacing={2} sx={{ pl: 2.5 }}>

                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.convenience_fee_indicator" fullWidth label="Convenience Fee Indicator" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.convenience_fee_indicator || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.convenience_fee_fixed" fullWidth label="Convenience Fee Fixed" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.convenience_fee_fixed || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.convenience_fee_percentage" fullWidth label="Convenience Fee Percentage" disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">%</InputAdornment> }}
                    value={dynamicInfo.convenience_fee_percentage || "N/A"} />
                </Grid>
                <Grid item xs={3} sx={{ mt: 2 }}>
                  <TextField comparisonKey="dynamic_info.is_autodebit_enabled" fullWidth label="AutoDebit Enabled" disabled
                    value={dynamicInfo.is_autodebit_enabled ? "Yes" : "No"} />
                </Grid>
                {dynamicInfo.is_autodebit_enabled && (
                  <Grid item xs={3} sx={{ mt: 2 }}>
                    <TextField comparisonKey="dynamic_info.autodebit_amount" fullWidth label="AutoDebit Amount" disabled
                      value={dynamicInfo.autodebit_amount || "N/A"} />
                  </Grid>
                )}

              </Grid>

            </Grid>
          )}

          {dynamicInfo && paymentHostName?.toLowerCase().startsWith(PAYMENT_HOST_LIST.DCI) && (
            <Grid
            container
            column={6}
            spacing={2}
            rowSpacing={4}
            sx={{
              height: "100%",
              p: 3,
            }}
          >
            <Grid item xs={3}>
              <TextField
                comparisonKey="dynamic_info.acquiring_institution_id"
                label="Acquiring Institution Id"
                disabled
                value={
                  dynamicInfo.acquiring_institution_id
                    ? dynamicInfo.acquiring_institution_id
                    : "N/A"
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                comparisonKey="dynamic_info.forwarding_institution_id"
                label="Forwarding Institution Id"
                disabled
                value={
                  dynamicInfo.forwarding_institution_id
                    ? dynamicInfo.forwarding_institution_id
                    : "N/A"
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                comparisonKey="dynamic_info.acceptor_id"
                label="Acceptor Id"
                disabled
                value={
                  dynamicInfo.acceptor_id
                    ? dynamicInfo.acceptor_id
                    : "N/A"
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                comparisonKey="dynamic_info.processing_code"
                label="Processing Code"
                disabled
                value={
                  dynamicInfo.processing_code
                    ? dynamicInfo.processing_code
                    : "N/A"
                }
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                comparisonKey="dynamic_info.dci_descriptor"
                label="DCI Descriptor"
                disabled
                value={
                  dynamicInfo.dci_descriptor
                    ? dynamicInfo.dci_descriptor
                    : "N/A"
                }
              />
            </Grid>
          </Grid>
        )}

          {dynamicInfo && paymentHostName.includes(PAYMENT_HOST_LIST.WORLDPAY) && (
            <Grid
              container
              column={6}
              spacing={2}
              sx={{
                p: 3,
              }}
            >
              <Grid item xs={3}>
                <FormControlLabel
                  comparisonKey="dynamic_info.enable_vis"
                  disabled
                  type="checkbox"
                  checked={dynamicInfo.vis_dynamic_info !== undefined}
                  control={<Checkbox />}
                  label="Enable VIS"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  comparisonKey="default_bin_routing"
                  disabled
                  type="checkbox"
                  checked={route.default_bin_routing}
                  control={<Checkbox />}
                  label="Default Bin Routing"
                />
              </Grid>
            </Grid>
          )}

          {dynamicInfo &&
            (paymentHostName.includes(PAYMENT_HOST_LIST.GRABPAY) ||
              paymentHostName.includes(PAYMENT_HOST_LIST.DUITNOW)) &&
            !paymentHostName.includes(PAYMENT_HOST_LIST.GRABPAY_GW2) &&
            !paymentHostName.includes(PAYMENT_HOST_LIST.GRABPAY_V1) && (
              <Grid
                container
                column={6}
                spacing={2}
                sx={{
                  p: 3,
                }}
              >
                {!paymentHostName?.toLowerCase().includes(PAYMENT_HOST_LIST.GRABPAY_SG) && (
                  <Grid item xs={3}>
                    <FormControlLabel
                      comparisonKey="dynamic_info.postpaid"
                      disabled
                      type="checkbox"
                      checked={dynamicInfo.postpaid}
                      control={<Checkbox />}
                      label="Postpaid"
                    />
                  </Grid>
                )}
                <Grid item xs={3}>
                  <FormControlLabel
                    comparisonKey="dynamic_info.installment_4"
                    sx={{ mx: 1 }}
                    disabled
                    type="checkbox"
                    checked={dynamicInfo.installment_4}
                    control={<Checkbox />}
                    label="Installment"
                  />
                </Grid>
                {paymentHostName?.toLowerCase().includes(PAYMENT_HOST_LIST.GRABPAY_SG) && dynamicInfo?.installment_4 && (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="charges.base_rate_installment"
                        label="Base Rate (Installment)"
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">%</InputAdornment>
                          ),
                        }}
                        value={
                          paymentRoute[0]?.base_rate_installment
                            ? toNumberInputString(paymentRoute[0].base_rate_installment)
                            : "N/A"
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        comparisonKey="charges.markup_rate_installment"
                        label="Markup Rate (Installment)"
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">%</InputAdornment>
                          ),
                        }}
                        value={
                          paymentRoute[0]?.markup_rate_installment
                            ? toNumberInputString(paymentRoute[0].markup_rate_installment)
                            : "N/A"
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            )}

          {route?.payment_host?.is_route_gw2 && (
            <Grid
              container
              column={12}
              spacing={2}
              sx={{
                p: 3
              }}
            >
              <Grid item xs={3}>
                <TextField
                  comparisonKey="gw2_proxy_route.gw2_params.mcptid"
                  label="MCPTID"
                  disabled
                  value={route.gw2_proxy_route?.gw2_params?.mcptid}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  comparisonKey="gw2_proxy_route.gw2_params.etype"
                  label="EType"
                  disabled
                  value={route.gw2_proxy_route?.gw2_params?.etype}
                />
              </Grid>

              <Grid item xs={4}>
                <FormControlLabel
                  comparisonKey="gw2_proxy_route.route_enabled"
                  disabled
                  type="checkbox"
                  checked={route.gw2_proxy_route?.route_enabled}
                  control={<Checkbox />}
                  label="Enable Route GW2"
                />
              </Grid>
            </Grid>
          )}

        </Grid>


        {route.action_message && (
          <Typography sx={{ mt: 3 }}>
            Action Message: {route.action_message}
          </Typography>
        )}
      </Card>
      </RouteComparisonContext.Provider>
    );

};

const TerminalDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [terminalDetails, setTerminalDetails] = useState();
  const [loaded, setLoaded] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  // Every maker-and-checker view: the pending dialog, the history dialog and the
  // standalone history page opened with ?action=true.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));

  useEffect(() => {
    if (!detailsData && !action) {
      getTerminalDetails(id, setTerminalDetails, setLoaded);
      return;
    }
    if (!detailsData && action) {
      getTerminalDetailsWithActions(id, setTerminalDetails, setLoaded);
      return;
    }

    setTerminalDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();
  const originalTerminal = terminalDetails?.original;
  const pendingAction = String(terminalDetails?.action || "").toLowerCase();
  const comparePendingTerminal = Boolean(isCheckerView && originalTerminal);
  const renderTerminalValue = (before, after, forceHighlight = false) =>
    comparePendingTerminal ? (
      <InlineComparison
        before={before}
        after={after}
        forceHighlight={forceHighlight}
      />
    ) : (
      displayComparisonValue(after)
    );

  const InfoColumn = () => {
    const pendingCredentials = terminalDetails?.operator_credentials;
    const originalCredentials = originalTerminal?.operator_credentials;
    const showPendingOperatorId =
      isCheckerView &&
      pendingAction === "update" &&
      pendingCredentials?.username !== null &&
      pendingCredentials?.username !== undefined &&
      pendingCredentials?.username !== "";
    const showPendingOperatorPassword =
      isCheckerView &&
      pendingAction === "update" &&
      pendingCredentials?.hashed_password !== null &&
      pendingCredentials?.hashed_password !== undefined &&
      pendingCredentials?.hashed_password !== "";

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Terminal ID</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {renderTerminalValue(
                originalTerminal?.terminal_id,
                terminalDetails?.terminal_id
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Merchant</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
              sx={{
                ...linkTextStyle
              }}
              component="span"
              onClick={() =>
                window.open(
                  `/3/app/merchant-details/${terminalDetails?.merchant?.id}/`,
                  "_blank"
                )
              }
            >
              {renderTerminalValue(
                originalTerminal?.merchant?.name,
                terminalDetails?.merchant?.name
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">MID</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {renderTerminalValue(
                originalTerminal?.merchant?.mid,
                terminalDetails?.merchant?.mid
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Mode</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {renderTerminalValue(
                originalTerminal?.mode?.mode,
                terminalDetails?.mode?.mode
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Business Entity</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
              sx={{
                ...linkTextStyle
              }}
              component="span"
              onClick={() =>
                window.open(
                  `/3/app/entity-details/${terminalDetails?.merchant?.business_entity.id}/`,
                  "_blank"
                )
              }
            >
              {renderTerminalValue(
                originalTerminal?.merchant?.business_entity?.business_name,
                terminalDetails?.merchant?.business_entity?.business_name
              )}
            </Grid>
            {(isCheckerView ? showPendingOperatorId : pendingCredentials) &&
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Operator ID</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {isCheckerView
                    ? <InlineComparison
                        before={originalCredentials?.username}
                        after={pendingCredentials?.username}
                      />
                    : pendingCredentials?.username}
                </Grid>
              </>
            }
            {(isCheckerView ? showPendingOperatorPassword : pendingCredentials) &&
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Operator Password</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {isCheckerView
                    ? <InlineComparison
                        before={originalCredentials?.hashed_password}
                        after={pendingCredentials?.hashed_password}
                      />
                    : pendingCredentials?.hashed_password?.startsWith("$argon2id$")
                    ? "******"
                    : pendingCredentials?.hashed_password}
                </Grid>
              </>
            }

            {action === "true" && !terminalDetails?.is_approved && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Rejection Reason</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {terminalDetails?.reject_message}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Active Now"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={terminalDetails?.is_approved ? "success" : "error"}
                >
                  {terminalDetails?.is_approved ? "Yes" : "No"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={terminalDetails?.is_active ? "success" : "error"}
                >
                  {terminalDetails?.is_active ? "YES" : "NO"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {terminalDetails?.created_at
                ? formatTimestamp(terminalDetails?.created_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {terminalDetails?.created_by
                ? terminalDetails?.created_by.email
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {terminalDetails?.approved_at
                ? formatTimestamp(terminalDetails?.approved_at)
                : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {terminalDetails?.approved_by
                ? terminalDetails?.approved_by.email
                : "N/A"}
            </Grid>
            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated on</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {terminalDetails.updated_at
                    ? parseDate(terminalDetails.updated_at)
                    : "N/A"}
                </Grid>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {terminalDetails?.updated_by
                    ? terminalDetails?.updated_by.email
                    : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  }

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Terminal Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {terminalDetails?.action}
        </Typography>
      )}
      {loaded && Boolean(terminalDetails?.original_unavailable) && (
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Before values are not available for this request because it was
          submitted before change logging was in place.
        </Typography>
      )}
      {loaded ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
            >
              {action && <DateColumn />}
              <InfoColumn />
              {!action && <DateColumn />}
            </Grid>
            {terminalDetails?.reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {terminalDetails?.reject_message}
                </Typography>
              </Typography>
            )}
            {terminalDetails?.action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {terminalDetails?.action_message}
                </Typography>
              </Typography>
            )}
          </Card>
          <Typography variant="h6" sx={{ my: 2 }}>
            Route Details
          </Typography>
          {loaded &&
            terminalDetails?.payment_mapping_terminal &&
            terminalDetails?.payment_mapping_terminal.map((route) => (
              <RouteDetail {...route} key={route.id} />
            ))}
          {loaded &&
            terminalDetails?.payment_mapping_pending_terminal &&
            terminalDetails?.payment_mapping_pending_terminal.map((route) => (
              <RouteDetail
                {...route}
                comparisonEnabled={Boolean(
                  isCheckerView &&
                  route.original &&
                  String(route.action || "").toLowerCase() === "update"
                )}
                comparisonOriginal={isCheckerView ? route.original : null}
                showActionNotice={Boolean(isCheckerView)}
                key={route.id}
              />
            ))}
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
};

export default TerminalDetails;
